import React from 'react';
import { Container, Typography, Grid, Box } from '@mui/material';
import { motion } from 'framer-motion';
import './CloudTechStack.css';

const technologies = {
  cloud: [
    { name: 'AWS', icon: '/assets/tech-logos/aws.png' },
    { name: 'Azure', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg' },
    { name: 'Google Cloud', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg' },
    // { name: 'IBM Cloud', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ibm/ibm-original.svg' },
    // { name: 'DigitalOcean', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/digitalocean/digitalocean-original.svg' },
    { name: 'Oracle Cloud', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/oracle/oracle-original.svg' }
  ],
  containerization: [
    { name: 'Docker', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
    { name: 'Kubernetes', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg' },
    { name: 'OpenShift', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redhat/redhat-original.svg' },
    // { name: 'Helm', icon: 'https://helm.sh/img/helm.svg' },
    // { name: 'Rancher', icon: 'https://www.vectorlogo.zone/logos/rancher/rancher-icon.svg' },
    // { name: 'Istio', icon: 'https://www.vectorlogo.zone/logos/istioio/istioio-icon.svg' }
  ],
  devops: [
    { name: 'Jenkins', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jenkins/jenkins-original.svg' },
    { name: 'GitLab CI', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg' },
    { name: 'GitHub Actions', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg' },
    { name: 'Terraform', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/terraform/terraform-original.svg' },
    // { name: 'Ansible', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ansible/ansible-original.svg' },
    // { name: 'ArgoCD', icon: 'https://www.vectorlogo.zone/logos/argoproj/argoproj-icon.svg' }
  ],
  monitoring: [
    { name: 'Prometheus', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/prometheus/prometheus-original.svg' },
    // { name: 'Grafana', icon: 'https://www.vectorlogo.zone/logos/grafana/grafana-icon.svg' },
    { name: 'ELK Stack', icon: 'https://www.vectorlogo.zone/logos/elastic/elastic-icon.svg' },
    // { name: 'Datadog', icon: 'https://www.vectorlogo.zone/logos/datadoghq/datadoghq-icon.svg' },
    { name: 'New Relic', icon: 'https://www.vectorlogo.zone/logos/newrelic/newrelic-icon.svg' },
    // { name: 'CloudWatch', icon: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original.svg' }
  ]
};

const categories = [
  { id: 'cloud', title: 'Cloud Platforms', subtitle: 'Leading cloud service providers' },
  { id: 'containerization', title: 'Containerization', subtitle: 'Container orchestration and management' },
  { id: 'devops', title: 'DevOps Tools', subtitle: 'Automation and continuous delivery' },
  { id: 'monitoring', title: 'Monitoring & Logging', subtitle: 'Observability and analytics' }
];

const CloudTechStack = () => {
  return (
    <section className="tech-stack">
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="tech-header"
        >
          <Typography variant="h2" className="section-title" gutterBottom>
            Our Cloud Technology Stack
          </Typography>
          <Typography variant="subtitle1" className="cloud-ts-section-subtitle" gutterBottom>
            Industry-leading technologies for robust cloud solutions
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {/* Cloud Platforms and Containerization sections */}
          {categories.slice(0, 2).map((category, categoryIndex) => (
            <Grid item xs={12} key={category.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
              >
                <Box className="category-container">
                  <Typography variant="h5" className="ts-category-title">
                    {category.title}
                  </Typography>
                  <Typography variant="subtitle2" className="category-subtitle">
                    {category.subtitle}
                  </Typography>
                  <Grid container spacing={2} className="tech-grid">
                    {technologies[category.id].map((tech, techIndex) => (
                      <Grid item xs={6} sm={4} md={2} key={tech.name}>
                        <motion.div
                          className="tech-item"
                          whileHover={{ y: -5 }}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                        >
                          <img
                            src={tech.icon}
                            alt={tech.name}
                            className="tech-icon"
                          />
                          <Typography variant="body2" className="tech-name">
                            {tech.name}
                          </Typography>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </motion.div>
            </Grid>
          ))}

          {/* DevOps and Monitoring sections side by side */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {categories.slice(2).map((category, categoryIndex) => (
                <Grid item xs={12} md={6} key={category.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (categoryIndex + 2) * 0.1 }}
                  >
                    <Box className="category-container" style={{ height: '100%' }}>
                      <Typography variant="h5" className="ts-category-title">
                        {category.title}
                      </Typography>
                      <Typography variant="subtitle2" className="category-subtitle">
                        {category.subtitle}
                      </Typography>
                      <Grid container spacing={2} className="tech-grid">
                        {technologies[category.id].map((tech, techIndex) => (
                          <Grid item xs={6} sm={4} key={tech.name}>
                            <motion.div
                              className="tech-item"
                              whileHover={{ y: -5 }}
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                            >
                              <img
                                src={tech.icon}
                                alt={tech.name}
                                className="tech-icon"
                              />
                              <Typography variant="body2" className="tech-name">
                                {tech.name}
                              </Typography>
                            </motion.div>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </section>
  );
};

export default CloudTechStack;